import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentEmployee, selectTaskLocations } from '../reducer';
import { selectEmployees } from '../../employees/reducer';
import { selectSiteTimeZone, selectSiteTotalCarCount } from '../../settings/reducer';
import { Task, TaskCompletion, TaskStatus } from '../models';
import { Interval } from '../models';
import { FancyCheckbox } from '../../../core/components/FancyCheckbox';
import { EmployeeInitialsDropdown } from '../../../core/components/EmployeeInitials';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import UserGroupPermissions, {
  editPermissionRefs,
  deletePermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import { toString } from '../../../core/utils';
import { getTask, saveEssentialTask, saveTask, deleteTask, deleteEssentialTask } from '../actions';
import {
  completeTaskCompletion,
  revertTaskCompletion,
  snoozeTaskCompletion,
} from '../../overview/actions';
import { useIntl } from 'react-intl';
import { useParams, useHistory } from 'react-router';
import { showDialog } from '../../../core/actions';
import { CgBlock } from 'react-icons/cg';

export enum TaskListDetail {
  None,
  Subtitle,
  Location,
}

interface TaskListItemProps {
  taskCompletion: TaskCompletion;
  detailType?: TaskListDetail;
  displayOptionsMenu?: boolean;
  displayTimeInfo?: boolean;
  className?: string;
  toggleCompletion?: any;
}

export const TaskListItem = ({
  taskCompletion,
  detailType = TaskListDetail.Subtitle,
  displayOptionsMenu = true,
  displayTimeInfo = true,
  className,
  toggleCompletion,
}: TaskListItemProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const params = useParams();
  const history = useHistory();
  const { id, status, employeeId, task } = taskCompletion;
  const [completed, setCompleted] = useState(status == Object.keys(TaskStatus)[2]);
  const [snoozed, setSnoozed] = useState(status == Object.keys(TaskStatus)[1]);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [overdue, setOverdue] = useState(false);
  const [time, setTime] = useState('');
  const timezone = useSelector(selectSiteTimeZone);
  const locations = useSelector(selectTaskLocations);
  const currentCarCount = useSelector(selectSiteTotalCarCount);
  const globalEmployee = useSelector(selectCurrentEmployee);
  const [currentEmployee, setCurrentEmployee] = useState(globalEmployee);

  const getTaskDetail = () => {
    if (detailType != TaskListDetail.None) {
      let details = null;
      if (detailType == TaskListDetail.Subtitle) {
        details = task.subTitle;
      }
      if (detailType == TaskListDetail.Location) {
        details = getLocation();
      }
      if (details) {
        return <span className="task-description">{details}</span>;
      }
    }
    return null;
  };

  const getLocation = () => {
    const location = locations.find((loc: any) => loc.type === task.locationId);
    return location ? location.message : '';
  };

  const getDueDate = () => {
    if (task.interval === toString(Interval, Interval.CarCount)) {
      if (currentCarCount >= taskCompletion.scheduleCarCount) {
        setOverdue(true);
      } else {
        setOverdue(false);
      }
      return intl.formatMessage(
        { id: 'dueAtCars' },
        { carCount: taskCompletion.scheduleCarCount.toLocaleString('en-US') }
      );
    }
    const due = taskCompletion.scheduleEnd
      ? new Date(taskCompletion.scheduleEnd)
      : new Date(taskCompletion.scheduleStart);
    setOverdue(due < new Date());
    return intl.formatMessage(
      { id: 'dueByTime' },
      {
        time: due.toLocaleDateString(undefined, {
          timeZone: timezone ?? undefined,
        }),
      }
    );
  };

  const getDoneDate = () => {
    setOverdue(false);
    if (task.interval === toString(Interval, Interval.CarCount)) {
      if (taskCompletion.carCount) {
        return intl.formatMessage(
          { id: 'doneAtCars' },
          { carCount: taskCompletion.carCount.toLocaleString('en-US') }
        );
      } else {
        return intl.formatMessage({ id: 'taskStatusDone' });
      }
    }
    return intl.formatMessage(
      { id: 'doneAtTime' },
      {
        time: new Date(taskCompletion.timestamp).toLocaleDateString(undefined, {
          timeZone: timezone ?? undefined,
        }),
      }
    );
  };

  const getTimeString = () => {
    return completed ? getDoneDate() : getDueDate();
  };

  const handleEmployeeSelect = (empId: number) => {
    setCurrentEmployee(empId);
  };

  const completeTask = () => {
    if (!completed) {
      dispatch(
        completeTaskCompletion({
          employeeId: currentEmployee,
          taskIds: [id],
          notes: {},
        })
      );
    } else {
      dispatch(
        revertTaskCompletion({
          employeeId: currentEmployee,
          taskIds: [id],
          notes: null,
        })
      );
    }
    if (toggleCompletion) toggleCompletion(id, currentEmployee);
    setCompleted(!completed);
  };

  const snoozeTask = () => {
    if (!snoozed) {
      dispatch(
        showDialog('SNOOZE_TASK', {
          taskCompletion: taskCompletion,
          dispatch: dispatch,
        })
      );
    } // else, waiting for unsnooze
  };

  const showTaskDetails = () => {
    dispatch(
      showDialog('VIEW_TASK_DETAILS', {
        title: intl.formatMessage({ id: 'taskDetails' }),
        taskCompletion: taskCompletion,
        match: { params: params },
        history: history,
        dispatch: dispatch,
      })
    );
  };

  const editDialogProps = {
    title: 'Edit Task',
    onSubmit: (editedTask: Task) => {
      return task?.groupId
        ? dispatch(
            saveEssentialTask({
              task: editedTask,
            })
          )
        : dispatch(
            saveTask({
              task: editedTask,
            })
          );
    },
    location: location,
    isNew: false,
    initialTask: task,
    isEssentialTask: task?.groupId ? true : false,
    modelYears: task?.modelYears,
  };

  const handleEditTask = () => {
    dispatch(getTask(task.id));
    dispatch(showDialog('UPSERT_TASK', editDialogProps));
  };

  const handleDeleteTask = () => {
    const { name, groupId } = task;
    const taskId = task?.id;
    const isEssentialTask = groupId ? true : false;
    const deletionParams = {
      taskId,
    };
    const confirmProps = {
      title: intl.formatMessage({ id: 'archiveTask' }),
      text: intl.formatMessage({ id: 'taskArchiveConfirmation' }, { name }),
      onReady: isEssentialTask
        ? () => dispatch(deleteEssentialTask(deletionParams))
        : () => dispatch(deleteTask(deletionParams)),
    };
    dispatch(showDialog('CONFIRM_DIALOG', confirmProps));
  };

  useEffect(() => {
    setTime(getTimeString());
    if (completed) {
      setCurrentEmployee(employeeId);
    } else {
      setCurrentEmployee(globalEmployee);
    }
  }, [id, completed, employeeId]);

  useEffect(() => {
    if (id && status) {
      setCompleted(status == Object.keys(TaskStatus)[2]);
      setSnoozed(status == Object.keys(TaskStatus)[1]);
    }
  }, [id, status]);

  return (
    <div className={`task${className ? ' ' + className : ''}${completed ? ' completed' : ''}`}>
      <div>
        {!snoozed ? (
          <FancyCheckbox
            isChecked={completed}
            onClick={completeTask}
            title={
              completed
                ? intl.formatMessage({ id: 'uncompleteTaskTitleText' })
                : intl.formatMessage({ id: 'completeTaskTitleText' })
            }
          />
        ) : (
          <div className="task-snoozed no-select">
            <CgBlock />
          </div>
        )}
        <button
          className="task-details"
          title={intl.formatMessage({ id: 'viewTaskDetails' })}
          onClick={showTaskDetails}
        >
          <span className="task-name">{task.name}</span>
          {getTaskDetail()}
        </button>
      </div>
      <div>
        {displayTimeInfo && <p className={`task-time ${overdue ? ' overdue' : ''}`}>{time}</p>}
        <EmployeeInitialsDropdown
          selectable={!completed}
          selectedEmployee={currentEmployee}
          onEmployeeSelect={handleEmployeeSelect}
          listHeight={150}
        />
        {displayOptionsMenu && (
          <Dropdown
            className="task-options-dropdown"
            isOpen={optionsOpen}
            toggle={() => setOptionsOpen(!optionsOpen)}
          >
            <DropdownToggle className="task-options">
              <i className="uil uil-ellipsis-v"></i>
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-right" right>
              <DropdownItem onClick={snoozeTask} disabled={completed}>
                {snoozed
                  ? intl.formatMessage({ id: 'unblock' })
                  : intl.formatMessage({ id: 'block' })}
              </DropdownItem>
              <UserGroupPermissions itemRef={editPermissionRefs.task}>
                <>
                  <DropdownItem divider />
                  <DropdownItem onClick={handleEditTask}>
                    {intl.formatMessage({ id: 'edit' })}
                  </DropdownItem>
                </>
              </UserGroupPermissions>
              <UserGroupPermissions itemRef={deletePermissionRefs.task}>
                <>
                  <DropdownItem divider />
                  <DropdownItem onClick={handleDeleteTask}>
                    {intl.formatMessage({ id: 'delete' })}
                  </DropdownItem>
                </>
              </UserGroupPermissions>
            </DropdownMenu>
          </Dropdown>
        )}
      </div>
    </div>
  );
};
